import { useState } from 'react';
import { useMutation } from 'react-query';
import { Box, CheckIcon, HStack, Pressable, VStack } from 'native-base';
import { filter, map, range, some } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@pimm/base';
import { CopyDailyPositionGoalsRequest, SmsWorkforceApi } from '@pimm/services/lib/sms-workforce';
import { useSiteTime } from '@app/features/store-core';
import { formatTo, formatToISOString } from '@app/utils/date-formatter';
import { useDailyPositionGoals } from '../context';

type CopyDailyGoalsProps = {
  onPressCancel?: () => void;
};

export const CopyDailyGoals = (props: CopyDailyGoalsProps) => {
  const { t } = useTranslation();
  const { dayBlocks, dailyGoals } = useDailyPositionGoals();
  const copyFromGoalDate = dayBlocks && dayBlocks[0].startTime;

  const siteTime = useSiteTime();
  const [today] = useState<Date>(siteTime.toDayBlockNow().startTime);
  const [week] = useState<ReturnType<ReturnType<typeof useSiteTime>['toStartEndOfWeek']>>(siteTime.toStartEndOfWeek(copyFromGoalDate));

  const [weekDays, setWeekDays] = useState<{ date: Date; isChecked: boolean; isDisabled?: boolean }[]>(
    range(7).map(day => {
      const date = moment(week?.startDate).add(day, 'days').toDate();

      return {
        date: date,
        isChecked: false,
        isDisabled:
          moment(date).isBefore(copyFromGoalDate || today, 'day') ||
          (moment(date).isSame(copyFromGoalDate || today, 'day') && moment(copyFromGoalDate).isSame(date, 'day')),
      };
    }),
  );

  const enableSave = some(weekDays, day => day.isChecked === true);

  const copyDailyGoals = useMutation({
    mutationFn: SmsWorkforceApi.CopyDailyPositionGoals,
    onSuccess: () => dailyGoals.refetch(),
  });

  const handlePressSelect = (index: number) => () => {
    // If the index is -1, select all
    setWeekDays(prev =>
      map(prev, (day, i) => {
        return {
          ...day,
          isChecked: (!day.isDisabled && index === -1) || day.isChecked !== (i === index),
        };
      }),
    );
  };

  const handlePressSave = async () => {
    const checkedDates = filter(weekDays, day => day.isChecked);

    if (checkedDates.length) {
      const payload: CopyDailyPositionGoalsRequest = {
        copyFromGoalDate: formatToISOString(copyFromGoalDate || today),
        copyToGoalDates: map(checkedDates, day => formatToISOString(day.date)),
        siteId: siteTime.siteId,
      };

      await copyDailyGoals.mutateAsync(payload);
      // Force close this modal
      if (props.onPressCancel) props.onPressCancel();
    }
  };

  return (
    <Box py={4} px={5}>
      <Box minHeight={10}>
        <Text size="xl" fontWeight={700} color="black">
          {`${t('ops_plan:copy_goals_from')} ${formatTo(copyFromGoalDate || today, 'MMM D')}`}
        </Text>
      </Box>

      <HStack alignItems="center" justifyContent="space-between">
        <Text size="md" fontWeight={500} color="gray.700">
          {t('ops_plan:select_dates_to_copy_goals_to')}:
        </Text>
        <Button size="sm" py={0} minH={7} h={8} background="primary.25" _text={{ color: 'primary.500' }} onPress={handlePressSelect(-1)}>
          {t('ops_plan:select_all')}
        </Button>
      </HStack>

      <HStack rounded="lg" my={2} borderWidth={1} overflow="hidden">
        {weekDays.map((day, index) => {
          return (
            <VStack flex={1} key={index} bg={day.isDisabled ? 'gray.25' : undefined}>
              <Box p={2} borderBottomWidth={1} borderLeftWidth={index ? 1 : 0}>
                <Text size="md" fontWeight={600} color="gray.700" textAlign="center" lineHeight="sm">
                  {`${formatTo(day.date, 'dddd')}\n${formatTo(day.date, 'MMM D')}`}
                </Text>
              </Box>
              <Box alignItems="center" justifyContent="center" py={3} borderLeftWidth={index ? 1 : 0}>
                <Pressable rounded="md" disabled={day.isDisabled} onPress={handlePressSelect(index)}>
                  {({ isHovered }) => (
                    <Box
                      rounded="md"
                      alignItems="center"
                      justifyContent="center"
                      w={6}
                      h={6}
                      borderWidth={1}
                      borderColor={day.isChecked || isHovered ? 'primary.500' : 'gray.300'}
                      bgColor={day.isChecked || isHovered ? 'primary.50' : day.isDisabled ? 'gray.100' : undefined}
                    >
                      <CheckIcon size={4} color="primary.500" opacity={day.isChecked ? 1 : isHovered ? 0.5 : 0} />
                    </Box>
                  )}
                </Pressable>
              </Box>
            </VStack>
          );
        })}
      </HStack>

      <Text size="md" fontWeight={500} color="gray.700">
        {t('ops_plan:future_dates_only_allowed')}.
      </Text>

      <HStack space={2} justifyContent="flex-end">
        <Button variant="unstyled" minWidth={120} onPress={props.onPressCancel}>
          {t('ops_plan:cancel')}
        </Button>
        <Button minWidth={120} disabled={!enableSave} isLoading={copyDailyGoals.isLoading} onPress={handlePressSave}>
          {t('ops_plan:save')}
        </Button>
      </HStack>
    </Box>
  );
};
